import { supabase } from "../supabase/SupabaseClient";
import { hoyAr } from "../shared/fechaAr";
import { traerTodas } from "./paginar";

/* Vigilancia — el control de acceso al portón (Nico, 23/09).
 *
 * ── DOS COSAS QUE NICO PIDIÓ EN EL MISMO MAIL ──────────────────────────────
 *
 * 1. La WHITELIST (`autorizaciones`): él deja anotado de antemano a quién deja
 *    entrar —un proveedor, un posible cliente, una visita: gente ajena a la
 *    empresa que tiene que justificar a qué viene—, por nombre y/o patente. Así
 *    Vigilancia no va por el predio preguntando quién está autorizado.
 * 2. El REGISTRO (`vigilancia_registros`): Vigilancia anota a TODOS los que
 *    entran y salen, con la salida, para saber quién está adentro ahora. De los
 *    desconocidos o proveedores no habituales toma el documento.
 *
 * ── EL DOLOR REAL: EN EL PORTÓN SÓLO SE VE LA PATENTE ──────────────────────
 *
 * Por eso `quienEsPatente` resuelve el nombre desde la patente: primero mira si
 * está en la whitelist (lo que Nico dejó anotado), y si es un vehículo de la
 * flota, lo cruza con la nómina por RPC (`chofer_de_patente`, definer y
 * gateada). Vigilancia teclea la patente y ya sabe quién es.
 *
 * ── EL CANDADO LO PONE LA BASE ─────────────────────────────────────────────
 *
 * Ver pide cualquiera de los tres permisos; registrar entradas/salidas y
 * manejar los AUTOS DEL PERSONAL (`personal = true`: alta, corrección, baja),
 * `cargar.vigilancia` —la llave de la barrera, desde el 13/10—; y las
 * autorizaciones de gente de afuera —alta, baja, editar, la validez—,
 * `autorizar.vigilancia`. Sin la llave la RLS rebota la escritura y las
 * lecturas vuelven vacías, aunque se llamen desde la consola. La whitelist y el registro son del sector, así que se escriben
 * directo (no como las visitas, que sólo escribe el bot).
 */

/* Patente como la guarda la base: SÓLO letras y números, en mayúscula.
 *
 * En la calle se escribe «KJQ-439», «kj q 439» o «AB.123.CD», y las tres son la
 * misma chapa. Antes se sacaban sólo los espacios: «KJQ-439» se guardaba con el
 * guion, no la encontraba quien buscaba «KJQ439» y el chequeo de «ya está en
 * la lista» la dejaba pasar dos veces. Es la misma regla con la que se busca
 * mientras se escribe (`patenteBusqueda`) y con la que entró la lista del
 * personal. */
export const patenteNorm = (p) =>
  String(p ?? "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");

/* Un error con un texto ya pensado para la barrera: la pantalla lo muestra tal
 * cual (ver `avisoDeError`). Los demás se cambian por uno propio. */
const errorParaMostrar = (texto) => Object.assign(new Error(texto), { paraMostrar: true });

/* 23505 = ya hay otra fila activa del personal con esa patente (índice único
 * de la migración 20261013223000). */
const esDuplicada = (error) => error?.code === "23505";

/* La base no deja que la barrera cargue como «del personal» una patente que
 * tiene una autorización de afuera, vigente o no: ésa la renueva quien
 * autoriza (migración 20261013223000). Desde el ticket 441 hay UNA lista de
 * autorizados, así que el texto habla de esa lista y no de «los autos del
 * personal», que era una lista aparte. */
export const PATENTE_DE_AFUERA =
  "Esa patente ya está en la lista de autorizados con una autorización de afuera " +
  "(vigente, vencida o dada de baja): la renueva quien autoriza.";
const esDeAfuera = (error) =>
  !!error && /autorizaci[oó]n de afuera/i.test(`${error.message || ""} ${error.details || ""}`);

export const PATENTE_YA_EN_LA_LISTA = "Esa patente ya está en la lista de autorizados.";

/* ── Whitelist: a quién dejó autorizado Nico ─────────────────────────────── */

const CAMPOS_AUTORIZACION = "id, nombre, patente, motivo, autorizado_por, desde, vence, fotos";

/* AUTOS DEL PERSONAL (Guillermo, Portería, tickets 389/394 — 13/10).
 *
 * `personal` marca los autos de la gente de la casa, que la barrera suma,
 * corrige y da de baja sola; `sector` es lo que antes se escribía a mano en
 * «a quién viene» en cada entrada. El DNI ya existía y se lee para poder
 * corregirlo (es optativo: «ese dato figura en otra base»).
 *
 * Van aparte de las columnas de siempre porque llegan con una migración
 * (20261013223000). Si la pantalla se publica antes de que esté aplicada, la
 * consulta con las columnas nuevas falla por la columna y se lee como antes:
 * el portón sigue andando, sin la lista del personal. */
const CAMPOS_PERSONAL = "personal, sector, dni, completado_por, completado_en, completado";

/* 42703 = la columna no existe (Postgres); PGRST204 = PostgREST no la ve. */
const faltaColumna = (error) =>
  !!error &&
  (error.code === "42703" || error.code === "PGRST204" || /column/i.test(error.message || ""));

/* Lee con las columnas del personal y, si la base todavía no las tiene, sin
 * ellas. `armar(columnas)` devuelve la consulta lista para esperar. */
async function conCamposPersonal(armar, base) {
  let { data, error } = await armar(`${base}, ${CAMPOS_PERSONAL}`);
  if (faltaColumna(error)) ({ data, error } = await armar(base));
  return { data, error };
}

/* En qué está una autorización: "vigente", "vencida" o "baja".
 *
 * Es la MISMA regla que filtran `autorizados()` y `quienEsPatente()` contra la
 * base, escrita para una fila que ya se tiene en la mano —la que pidió el
 * buscador, la de la lista de no vigentes—. La baja gana sobre el vencimiento:
 * si alguien la sacó a mano, eso es lo que hay que contar, aunque además
 * estuviera vencida. `vence` incluye su día. */
export function estadoAutorizacion(a, hoy = hoyAr()) {
  if (!a) return null;
  if (a.activo === false) return "baja";
  if (a.vence && String(a.vence).slice(0, 10) < hoy) return "vencida";
  return "vigente";
}

/* Una autorización sola, por id, ESTÉ O NO VIGENTE. La pide el buscador
 * (`?ver=`), que también encuentra las dadas de baja y las vencidas: la lista
 * de arriba las esconde, y sin esto el link llevaría a una pantalla donde lo
 * buscado no está. Es para MIRAR — el portón sigue preguntando por las
 * vigentes en `quienEsPatente`. */
export async function autorizacion(id) {
  const { data, error } = await conCamposPersonal(
    (cols) => supabase.from("autorizaciones").select(cols).eq("id", id).maybeSingle(),
    `${CAMPOS_AUTORIZACION}, activo, baja_por, baja_en`,
  );
  if (error) throw error;
  return data || null;
}

/* Los autorizados VIGENTES, del más nuevo al más viejo.
 *
 * Dos formas de no estar vigente, y las dos se filtran acá:
 *
 *   · dado de baja a mano → `activo = false`. La baja no borra: queda el
 *     registro de que existió y de quién la dio.
 *   · VENCIDO → `vence` es anterior a hoy. Nico, 29/09: «los ingresos
 *     autorizados tienen que tener una fecha de expiración así se dan de baja
 *     automáticamente». Hasta ahora `vence` se guardaba y no lo miraba nadie,
 *     así que una autorización de hace un mes seguía habilitando hoy.
 *
 * SE VENCE AL LEER Y NO CON UNA TAREA QUE APAGUE FILAS. Un proceso nocturno que
 * ponga `activo=false` agrega una pieza que se puede caer —y el día que no
 * corra, el portón deja entrar a alguien que no tenía que entrar— y además
 * pisa el único campo que dice si ALGUIEN la dio de baja. Comparando contra la
 * fecha, la lista está bien aunque no corra nada.
 *
 * El día es el ARGENTINO. Con el `today()` del servidor, entre las 21 y las 00
 * de acá ya es mañana en UTC: una autorización que vence hoy dejaría de valer
 * tres horas antes de que termine el día para el que se dio.
 *
 * `vence` INCLUYE su día: «vence el 30» significa que el 30 todavía entra. */
export async function autorizados() {
  const { data, error } = await conCamposPersonal(
    (cols) =>
      supabase
        .from("autorizaciones")
        .select(cols)
        .eq("activo", true)
        .or(`vence.is.null,vence.gte.${hoyAr()}`)
        .order("desde", { ascending: false }),
    CAMPOS_AUTORIZACION,
  );
  if (error) throw error;
  return data || [];
}

/* Las que YA NO valen: dadas de baja a mano o vencidas (P-308, Nico 05/10).
 *
 * Van aparte de las vigentes y no mezcladas: en el portón la pregunta es
 * «¿lo dejo pasar?», y una fila vencida al lado de una vigente invita a
 * leerla mal. Pero tampoco desaparecen: Nico quiere ver a quién sacó y qué
 * se venció, y el registro de accesos sigue apuntando a ellas.
 *
 * Es la condición contraria exacta a la de `autorizados()` —`activo=false` o
 * `vence` antes de hoy—, así que cada autorización está en una lista o en la
 * otra, nunca en las dos ni en ninguna. Con tope: es para mirar lo reciente,
 * lo viejo lo encuentra el buscador. */
export const TOPE_NO_VIGENTES = 50;

export async function autorizadosNoVigentes(tope = TOPE_NO_VIGENTES) {
  const { data, error } = await conCamposPersonal(
    (cols) =>
      supabase
        .from("autorizaciones")
        .select(cols)
        .or(`activo.eq.false,vence.lt.${hoyAr()}`)
        .order("desde", { ascending: false })
        .limit(tope),
    `${CAMPOS_AUTORIZACION}, activo, baja_por, baja_en`,
  );
  if (error) throw error;
  return data || [];
}

/* Alta de un autorizado. Al menos nombre o patente; el motivo es la
 * justificación —lo que Vigilancia necesita para dejar entrar a un externo. */
export async function autorizar({ nombre, patente, motivo, autorizadoPor,
                                 vence }) {
  const fila = {
    nombre: String(nombre ?? "").trim() || null,
    patente: patenteNorm(patente) || null,
    motivo: String(motivo ?? "").trim() || null,
    autorizado_por: String(autorizadoPor ?? "").trim() || null,
    // `undefined` es «no me lo pasaron» y `null` es «sin vencimiento», y no
    // son lo mismo: el primero no tiene que tocar la columna.
    ...(vence === undefined ? {} : { vence: vence || null }),
  };
  const { error } = await supabase.from("autorizaciones").insert(fila);
  if (error) throw error;
}

/* Editar una autorización que ya existe: sobre todo para SUMARLE la patente
 * cuando llega más tarde (Nico, 24/09), pero también el nombre, el motivo o
 * HASTA CUÁNDO VALE (P-308: «tiene que dejarme poner una validez»). Sólo se
 * tocan los campos que se pasan; la patente se normaliza como en el alta.
 *
 * `vence` sigue la misma regla que en `autorizar`: `undefined` no toca la
 * columna, vacío o `null` la deja SIN vencimiento. */
function cambiosDeAutorizacion({ nombre, patente, motivo, vence, sector, dni } = {}) {
  const cambios = {};
  if (nombre !== undefined) cambios.nombre = String(nombre ?? "").trim() || null;
  if (patente !== undefined) cambios.patente = patenteNorm(patente) || null;
  if (motivo !== undefined) cambios.motivo = String(motivo ?? "").trim() || null;
  if (vence !== undefined) cambios.vence = vence || null;
  // Del auto del personal (lo corrige la barrera): el sector y el DNI, que es
  // optativo y vacío queda en null.
  if (sector !== undefined) cambios.sector = String(sector ?? "").trim() || null;
  if (dni !== undefined) cambios.dni = String(dni ?? "").trim() || null;
  return cambios;
}

/* El que se repite y el de afuera vuelven con un texto para la pantalla. */
function errorDeEscritura(error) {
  if (esDuplicada(error)) return errorParaMostrar(PATENTE_YA_EN_LA_LISTA);
  if (esDeAfuera(error)) return errorParaMostrar(PATENTE_DE_AFUERA);
  return error;
}

export async function editarAutorizacion(id, campos) {
  const cambios = cambiosDeAutorizacion(campos);
  if (Object.keys(cambios).length === 0) return;
  const { error } = await supabase
    .from("autorizaciones")
    .update(cambios)
    .eq("id", id);
  if (error) throw errorDeEscritura(error);
}

/* REACTIVAR una dada de baja (Nico, ticket 441: un solo listado, con los
 * dados de baja adentro). Vuelve a valer la MISMA fila —las entradas que
 * quedaron ligadas a ella siguen apuntándole— y, de paso, se corrige lo que
 * haga falta (por ejemplo, hasta cuándo vale). El sello de la baja lo borra la
 * base (`autorizaciones_sellar`): una baja que ya no rige no queda cargada.
 *
 * Los mismos candados que la baja: la RLS deja reactivar lo que deja editar
 * (del personal, la barrera; lo de afuera, quien autoriza), y SIN LA LLAVE NO
 * DA ERROR —filtra la fila y no toca nada—, así que se pide la fila de vuelta.
 * El `activo=false` evita reactivar dos veces. Si la patente ya volvió a
 * estar en la lista del personal, el índice único lo rebota. */
export async function reactivarAutorizacion(id, campos) {
  const { data, error } = await supabase
    .from("autorizaciones")
    .update({ ...cambiosDeAutorizacion(campos), activo: true })
    .eq("id", id)
    .eq("activo", false)
    .select("id");
  if (error) throw errorDeEscritura(error);
  if (!data || data.length === 0) {
    throw errorParaMostrar(
      "No se reactivó: o ya estaba vigente, o no tenés el permiso.",
    );
  }
}

/* ── Sumar un auto del personal (Guillermo, tickets 389/394) ──────────────
 *
 * Lo hace la barrera, con su llave de siempre: la base deja escribir
 * `personal = true` a quien registra entradas (migración 20261013223000), y
 * las autorizaciones de afuera siguen siendo de quien autoriza.
 *
 * PATENTE Y NOMBRE, LOS DOS. La patente es por donde se lo va a buscar en el
 * portón y el nombre es lo que tiene que aparecer al escribirla: sin uno de los
 * dos, la próxima vez no se completa nada. El sector y el DNI son optativos.
 *
 * SIN DUPLICAR. Si esa patente ya está vigente en la lista (del personal o de
 * afuera), no se carga otra vez: se devuelve la que estaba, con `yaEstaba`, y
 * la pantalla lo dice. Se compara normalizada (sólo letras y números), como la
 * guarda la base; y si dos altas de la misma patente llegan a la vez, la base
 * deja entrar una sola (índice único) y la otra vuelve como `yaEstaba`.
 *
 * NO PISA UNA AUTORIZACIÓN DE AFUERA. Si la patente tiene una de afuera
 * —vigente, vencida o dada de baja—, la base rebota el alta a quien no
 * autoriza: si no, una autorización de un día se volvía «del personal» y sin
 * vencimiento.
 *
 * Sin vencimiento: el auto de alguien de la casa vale hasta que se lo dé de
 * baja, que es justo lo que la barrera puede hacer. */
export const FALTA_PATENTE_O_NOMBRE = "Poné la patente y el nombre.";

export async function sumarAutoPersonal({ patente, nombre, sector, dni }) {
  const p = patenteNorm(patente);
  const n = String(nombre ?? "").trim();
  if (!p || !n) throw errorParaMostrar(FALTA_PATENTE_O_NOMBRE);

  const { data: ya, error: errYa } = await supabase
    .from("autorizaciones")
    .select("id, nombre, patente")
    .eq("activo", true)
    .or(`vence.is.null,vence.gte.${hoyAr()}`)
    .filter("patente", "eq", p)
    .limit(1);
  if (errYa) throw errYa;
  if (ya && ya.length) return { id: ya[0].id, nombre: ya[0].nombre || "", yaEstaba: true };

  const fila = {
    nombre: n,
    patente: p,
    sector: String(sector ?? "").trim() || null,
    dni: String(dni ?? "").trim() || null,
    personal: true,
    vence: null,
  };
  const { data, error } = await supabase
    .from("autorizaciones")
    .insert(fila)
    .select("id")
    .single();
  if (error) {
    if (esDuplicada(error)) return { id: null, nombre: "", yaEstaba: true };
    if (esDeAfuera(error)) throw errorParaMostrar(PATENTE_DE_AFUERA);
    // Sin la llave la base rebota el alta: se dice qué pasó, no el error crudo.
    if (error.code === "42501" || /row-level security/i.test(error.message || "")) {
      throw errorParaMostrar("No se pudo sumar: tu usuario no puede cargar en Autos Autorizados.");
    }
    throw error;
  }
  return { id: data?.id ?? null, nombre: n, yaEstaba: false };
}

/* ── La foto del autorizado (Nico, ticket 290) ────────────────────────────
 *
 * La saca Vigilancia en el portón —la persona o el auto— y queda colgada de la
 * autorización. Se sube al bucket `vigilancia` y la fila guarda la RUTA, nunca
 * una URL firmada, que se vence.
 *
 * ── LA RUTA SE ANOTA CON UNA FUNCIÓN, NO CON UN UPDATE (P-308) ───────────
 *
 * Antes se hacía `update({ fotos })` sobre la fila. Pero el UPDATE de
 * `autorizaciones` pide `autorizar.vigilancia`, y quien saca la foto es
 * Vigilancia (`cargar.vigilancia`): la RLS filtraba la fila, el update no
 * tocaba nada y NO DABA ERROR. La pantalla seguía como si hubiera salido bien
 * y la foto quedaba suelta en el bucket.
 *
 * `autorizacion_poner_foto` (definer) deja a Vigilancia agregar la foto y
 * nada más —no dar de baja ni cambiar la validez—, valida que la ruta sea de
 * esta autorización y la agrega sobre lo que hay en la base, así dos fotos
 * casi juntas no se pisan. Devuelve la lista como quedó. Si no devuelve una
 * lista, NO se guardó, y eso se dice: un «listo» mudo es justo el error que
 * había. */
export const BUCKET_VIGILANCIA = "vigilancia";

export const NO_SE_GUARDO_LA_FOTO =
  "La foto no quedó guardada en la autorización. Probá de nuevo; si sigue, avisá.";

/* La extensión del archivo, o "jpg". Un nombre sin punto («IMG_2034») no
 * tiene extensión: sin esto quedaba «img2034» como extensión. */
function extDe(nombre) {
  const s = String(nombre || "");
  const i = s.lastIndexOf(".");
  if (i < 0) return "jpg";
  const ext = s.slice(i + 1).toLowerCase().replace(/[^a-z0-9]/g, "");
  return ext && ext.length <= 5 ? ext : "jpg";
}

export async function subirFotoAutorizacion(autorizacion, archivo) {
  if (!autorizacion?.id || !archivo) throw new Error("Falta la foto.");
  const ruta = `autorizaciones/${autorizacion.id}/${Date.now()}.${extDe(archivo.name)}`;
  const { error: errSubida } = await supabase.storage
    .from(BUCKET_VIGILANCIA)
    .upload(ruta, archivo, { contentType: archivo.type || "image/jpeg" });
  if (errSubida) throw errSubida;
  const { data, error } = await supabase.rpc("autorizacion_poner_foto", {
    p_id: autorizacion.id,
    p_ruta: ruta,
  });
  if (error || !Array.isArray(data)) {
    // Sin la fila no hay quién la encuentre: se intenta no dejarla suelta.
    await supabase.storage.from(BUCKET_VIGILANCIA).remove([ruta]).catch(() => {});
    const detalle = error?.message ? ` (${error.message})` : "";
    throw new Error(`${NO_SE_GUARDO_LA_FOTO}${detalle}`);
  }
  return data;
}

/* Link firmado por un rato para mirar una foto. `null` si no se pudo. */
export async function linkFotoVigilancia(ruta, segundos = 3600) {
  if (!ruta) return null;
  const { data, error } = await supabase.storage
    .from(BUCKET_VIGILANCIA)
    .createSignedUrl(ruta, segundos);
  return error ? null : data?.signedUrl || null;
}

/* Baja: deja de estar autorizado YA, pero la fila no se borra (P-308).
 *
 * El registro de accesos apunta a la autorización con la que entró cada uno:
 * borrarla dejaría ese historial sin referencia. Quién y cuándo los sella la
 * base (trigger `autorizaciones_sellar`): la hora del servidor y el mail de la
 * sesión, que no se pueden falsear desde acá.
 *
 * SIN LA LLAVE LA BASE NO DA ERROR: la RLS filtra la fila y el update no toca
 * nada. Por eso se pide la fila de vuelta y, si no vino, se dice — si no, el
 * botón «funcionaría» y la persona seguiría autorizada. El `activo=true` evita
 * que un segundo click mueva la hora de una baja que ya estaba. */
export async function bajaAutorizacion(id) {
  const { data, error } = await supabase
    .from("autorizaciones")
    .update({ activo: false })
    .eq("id", id)
    .eq("activo", true)
    .select("id");
  if (error) throw error;
  if (!data || data.length === 0) {
    throw errorParaMostrar(
      "No se dio de baja: o ya no estaba vigente, o no tenés el permiso para autorizar.",
    );
  }
}

/* ── Quién es esta patente ────────────────────────────────────────────────
 *
 * Devuelve `{ nombre, fuente, autorizado, motivo, autorizacionId }`. `fuente`
 * dice de dónde salió el nombre: "whitelist" (lo anotó Nico) o "flota" (cruce
 * con la nómina). `autorizado` marca si está en la whitelist activa, y
 * `autorizacionId` es la fila de esa autorización —para LIGAR el ingreso a ella
 * cuando el que llega venía autorizado de antemano—. Sin match, nombre vacío.
 */
export async function quienEsPatente(patente) {
  const p = patenteNorm(patente);
  if (p.length < 4)
    return { nombre: "", fuente: null, autorizado: false, motivo: "", autorizacionId: null };

  // 1) La whitelist: lo que Nico dejó anotado gana, porque es lo que él decidió.
  //
  // ── Y VENCIDA NO ES AUTORIZADA ────────────────────────────────────────
  // Acá es donde el vencimiento importa de verdad. La lista de autorizados
  // es informativa; ESTO es lo que le contesta al del portón «sí, pasá»
  // cuando teclea la patente. Si la lista escondiera las vencidas y esta
  // consulta no, el vencimiento sería un adorno: la autorización
  // desaparecería de la pantalla y seguiría abriendo el portón.
  const { data: wl } = await supabase
    .from("autorizaciones")
    .select("id, nombre, motivo, autorizado_por")
    .eq("activo", true)
    .or(`vence.is.null,vence.gte.${hoyAr()}`)
    .filter("patente", "eq", p)
    .limit(1);
  const autz = wl && wl.length ? wl[0] : null;
  if (autz && String(autz.nombre ?? "").trim()) {
    return { nombre: autz.nombre, fuente: "whitelist", autorizado: true,
             motivo: autz.motivo || "", autorizacionId: autz.id,
             autorizadoPor: autz.autorizado_por || "" };
  }
  // Autorizada por patente pero sin nombre cargado: ya sabemos que está
  // autorizada y con qué fila, pero seguimos al cruce con la flota por el nombre.
  const autorizado = !!autz;
  const autorizacionId = autz ? autz.id : null;
  // Quién la autorizó, para que el aviso del portón no nombre a nadie de memoria.
  const autorizadoPor = autz ? autz.autorizado_por || "" : "";

  // 2) Vehículo de la flota: cruce con la nómina (definer + gate de permiso).
  try {
    const { data } = await supabase.rpc("chofer_de_patente", { p_patente: p });
    const row = Array.isArray(data) ? data[0] : data;
    if (row && String(row.chofer ?? "").trim()) {
      return { nombre: row.chofer, fuente: "flota", autorizado, motivo: "", autorizacionId, autorizadoPor };
    }
  } catch {
    /* Sin permiso o sin match: no es un error de la pantalla, sigue sin nombre. */
  }
  return { nombre: "", fuente: null, autorizado, motivo: "", autorizacionId, autorizadoPor };
}

/* ── Registro de entradas y salidas ──────────────────────────────────────── */

/* Quién está adentro AHORA: entró y todavía no salió. */
export async function adentroAhora() {
  const { data, error } = await supabase
    .from("vigilancia_registros")
    .select("id, nombre, dni, patente, a_quien, como, entrada, autorizacion_id")
    .is("salida", null)
    .order("entrada", { ascending: false });
  if (error) throw error;
  return data || [];
}

/* Los movimientos de un rango, del más nuevo al más viejo. Se pide un rango y
 * no «los últimos N»: la pregunta es «¿quién entró hoy?», y un tope contestaría
 * distinto según cuánta gente vino. */
export async function movimientos(desde, hasta) {
  return traerTodas(() =>
    supabase
      .from("vigilancia_registros")
      .select(
        "id, nombre, dni, patente, a_quien, como, entrada, salida, " +
          "registrado_por, autorizacion_id",
        { count: "exact" },
      )
      .gte("entrada", desde)
      .lt("entrada", hasta)
      .order("entrada", { ascending: false })
      .order("id", { ascending: false }),
  );
}

/* Las entradas de autos de los últimos días, para ofrecerlos al escribir.
 *
 * Son los «faltan algunos» de Guillermo: el auto que ya entró y todavía no
 * está en la lista aparece igual mientras se escribe su patente o su nombre,
 * marcado como «no está en autorizados», y al registrarlo se ofrece sumarlo.
 * Es una ayuda: si no se puede leer, el formulario sigue con la lista. */
export const DIAS_RECIENTES = 45;

export async function entradasRecientes(dias = DIAS_RECIENTES) {
  const desde = new Date(Date.now() - dias * 24 * 3600 * 1000).toISOString();
  const { data, error } = await supabase
    .from("vigilancia_registros")
    .select("id, nombre, patente, a_quien, entrada")
    .gte("entrada", desde)
    .not("patente", "is", null)
    .order("entrada", { ascending: false })
    .limit(500);
  if (error) throw error;
  return data || [];
}

/* Anota una entrada. Al menos nombre o patente. `como` es 'auto' | 'caminando'
 * —Nico: «tanto sea con auto o caminando». */
/* ── La barrera completa lo que faltaba (Nico, ticket 521, 26/10) ─────────
 *
 * «¿Tiene la vigilancia alguna manera de completar datos de los autos que yo
 * dejé incompletos por desconocimiento?». Nico autoriza a veces sólo por
 * nombre; la patente y el documento los ve la barrera cuando el auto llega.
 * Registrada la entrada de una autorización a la que le faltaba la patente o
 * el DNI, lo que se anotó queda guardado en la autorización.
 *
 * Va por una función de la base (`autorizacion_completar`, migración
 * 20261027120000) y no por un update: la barrera no edita autorizaciones de
 * afuera. La función SÓLO completa campos vacíos —lo que cargó Nico no se
 * pisa— y anota quién lo completó. Qué falta lo decide `faltaCompletar`
 * (shared/autosAutorizados). Devuelve qué campos completó
 * (`["patente"]`, `["dni"]`, los dos o ninguno). */
export async function completarAutorizacion(id, { patente, dni } = {}) {
  const { data, error } = await supabase.rpc("autorizacion_completar", {
    p_id: id,
    p_patente: patente || null,
    p_dni: dni || null,
  });
  if (error) throw error;
  return Array.isArray(data?.completado) ? data.completado : [];
}

export async function registrarEntrada({
  nombre,
  dni,
  patente,
  aQuien,
  como,
  autorizacionId,
  registradoPor,
}) {
  const fila = {
    nombre: String(nombre ?? "").trim() || null,
    dni: String(dni ?? "").trim() || null,
    patente: patenteNorm(patente) || null,
    a_quien: String(aQuien ?? "").trim() || null,
    como: como || null,
    autorizacion_id: autorizacionId || null,
    registrado_por: String(registradoPor ?? "").trim() || null,
  };
  const { data, error } = await supabase
    .from("vigilancia_registros")
    .insert(fila)
    .select("id")
    .single();
  if (error) throw error;
  return data;
}

/* Marca la salida ahora. Sólo pisa filas que siguen adentro, para que dos
 * clicks no muevan la hora ya registrada. */
export async function registrarSalida(id) {
  const { error } = await supabase
    .from("vigilancia_registros")
    .update({ salida: new Date().toISOString() })
    .eq("id", id)
    .is("salida", null);
  if (error) throw error;
}
